import { zodResolver } from "@hookform/resolvers/zod";
import { memo, useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { editUserSchema, UserFormData } from "../model/schemas";

import { UserForm } from "./UserForm";

import {
  CreateUserDTORole,
  useEditUserData,
  useGetUserData,
  type UserDataDTO,
  type UserEditDataDTO,
} from "@/common/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/common/components";
import { useToast } from "@/common/hooks";
import { ROLE_LABELS } from "@/common/model";

const ROLE_OPTIONS = [
  {
    value: CreateUserDTORole.CARGO_OWNER,
    label: ROLE_LABELS.cargo_owner,
  },
  {
    value: CreateUserDTORole.WAGON_OWNER,
    label: ROLE_LABELS.wagon_owner,
  },
  {
    value: CreateUserDTORole.ADMIN,
    label: ROLE_LABELS.admin,
  },
] as const;

interface EditUserDialogProps {
  userId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const EditUserDialog = memo(function EditUserDialog({
  userId,
  isOpen,
  onClose,
  onSuccess,
}: EditUserDialogProps) {
  const { toast } = useToast();
  const [isEditing, setIsEditing] = useState(false);

  const editForm = useForm<UserFormData>({
    resolver: zodResolver(editUserSchema),
    mode: "onChange",
    defaultValues: {
      login: "",
      passwordHash: "",
      username: "",
      role: ROLE_OPTIONS.map((o) => o.value)[0],
      companyName: "",
      inn: "",
      kpp: "",
      ogrn: "",
      email: "",
      contactNumber: "",
      companyEmail: "",
      companyContactNumber: "",
      addressLegal: "",
      addressActual: "",
    },
  });

  const { data: userDataResponse } = useGetUserData(
    { userId: String(userId) },
    {
      query: {
        enabled: isOpen && !!userId,
        staleTime: 0,
        refetchOnMount: true,
      },
    },
  );

  const userPayload: UserDataDTO | undefined = userDataResponse?.data?.payload;

  // Мутация для редактирования пользователя
  const { mutateAsync: editUserMutate } = useEditUserData();

  useEffect(() => {
    if (!isOpen) return;

    if (!userPayload) {
      // Если по какой‑то причине данных нет, оставляем форму пустой
      // Данные будут загружены при следующем запросе
      return;
    }

    // Преобразуем роль из строки в enum
    let role: CreateUserDTORole = CreateUserDTORole.CARGO_OWNER;
    const roleValue = userPayload.role?.toString().toUpperCase();
    if (roleValue === "CARGO_OWNER" || roleValue === CreateUserDTORole.CARGO_OWNER) {
      role = CreateUserDTORole.CARGO_OWNER;
    } else if (roleValue === "WAGON_OWNER" || roleValue === CreateUserDTORole.WAGON_OWNER) {
      role = CreateUserDTORole.WAGON_OWNER;
    } else if (roleValue === "ADMIN" || roleValue === CreateUserDTORole.ADMIN) {
      role = CreateUserDTORole.ADMIN;
    }

    editForm.reset({
      login: userPayload.login ?? userPayload.username ?? "",
      passwordHash: userPayload.password ?? "",
      username: userPayload.username ?? "",
      role,
      companyName: userPayload.companyName ?? "",
      inn: userPayload.inn ?? "",
      kpp: userPayload.kpp ?? "",
      ogrn: userPayload.ogrn ?? "",
      email: userPayload.email ?? "",
      contactNumber: userPayload.contactNumber ?? "",
      companyEmail: userPayload.companyEmail ?? "",
      companyContactNumber: userPayload.companyContactNumber ?? "",
      addressLegal: userPayload.addressLegal ?? "",
      addressActual: userPayload.addressActual ?? "",
    });
  }, [isOpen, userId, userPayload, editForm]);

  const onEditSubmit = useCallback(
    (data: UserFormData) => {
      setIsEditing(true);
      editUserMutate({
        data: {
          id: userId,
          username: data.username || undefined,
          password: data.passwordHash || undefined,
          email: data.email || undefined,
          contactNumber: data.contactNumber || undefined,
          inn: data.inn,
          name: data.companyName || undefined,
          kpp: data.kpp || undefined,
          ogrn: data.ogrn || undefined,
          companyContactNumber: data.companyContactNumber || undefined,
          addressLegal: data.addressLegal || undefined,
          addressActual: data.addressActual || undefined,
          companyEmail: data.companyEmail || undefined,
        } satisfies UserEditDataDTO,
      })
        .then(() => {
          onClose();
          onSuccess();
          toast({
            title: "Успешно",
            description: "Данные пользователя обновлены",
          });
        })
        .catch((error: unknown) => {
          toast({
            variant: "destructive",
            title: "Ошибка обновления пользователя",
            description:
              error instanceof Error ? error.message : "Не удалось обновить данные пользователя",
          });
        })
        .finally(() => {
          setIsEditing(false);
        });
    },
    [editUserMutate, userId, onClose, onSuccess, toast],
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Редактирование пользователя</DialogTitle>
        </DialogHeader>

        <UserForm
          mode="edit"
          form={editForm}
          onSubmit={onEditSubmit}
          isSubmitting={isEditing}
          submitLabel="Сохранить"
          submittingLabel="Сохранение..."
          onCancel={onClose}
        />
      </DialogContent>
    </Dialog>
  );
});
